/**
 * Calcula qué acciones de transición están disponibles para una orden, según su
 * estado, su estado de pago y los permisos del usuario.
 *
 * Une las dos puertas que decide el backend (`progress/explore_orders_api.md` y
 * `..._ssr.md`):
 *  1. **Regla de estado** (`ALLOWED_ACTIONS`): qué transiciones permite el estado
 *     actual; `deliver` exige además que la orden esté pagada (`isPaidForDelivery`).
 *  2. **Permiso** (`ACTION_PERMISSION`): el MISMO permiso que exige la API. Sin
 *     él, la acción no se ofrece (se oculta), igual que en el SSR.
 *
 * Función pura (no es un hook): recibe la sesión ya resuelta (`hasPermission`) y
 * devuelve la lista de acciones a pintar. La pantalla añade la confirmación y la
 * mutación.
 */
import { ACTION_PERMISSION, ALLOWED_ACTIONS, isPaidForDelivery } from "./constants";
import type { OrderAction, OrderStatus, PaymentStatus } from "./types";

interface AvailableActionsInput {
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  hasPermission: (permission: string) => boolean;
}

/** Acciones de transición disponibles (por estado + pago + permiso). */
export function getAvailableActions({
  status,
  paymentStatus,
  hasPermission,
}: AvailableActionsInput): OrderAction[] {
  return ALLOWED_ACTIONS[status].filter((action) => {
    // Entregar solo si la orden está pagada (regla de negocio del backend).
    if (action === "deliver" && !isPaidForDelivery(paymentStatus)) {
      return false;
    }
    // Cada acción exige el MISMO permiso que la API.
    return hasPermission(ACTION_PERMISSION[action]);
  });
}
